<template>
  <transition
    v-if="!noContent"
    :name="transitionName"
  >
    <div
      v-show="isActive"
      v-cloak
      ref="overlay"
      class="overlay critical-hide"
      :aria-label="overlayLabel ? overlayLabel : null"
      :aria-labelledby="$slots.title && !overlayLabel ? `${namespace}-title` : null"
      aria-modal="true"
      :class="[
        {
          'overlay--has-drag': mobileDragClose,
          'overlay--has-sticky-header': hasStickyHeader,
          'overlay--open': isActive,
        },
        classContainer,
        $blockClass(blockClass),
        responsiveType,
        `${responsiveType}--${responsiveDirection}`,
      ]"
      role="dialog"
      @touchstart="dragTouchStart"
      @touchmove="dragTouchMoved"
      @touchend="dragTouchEnded"
    >
      <div
        v-if="hasStickyHeader"
        class="overlay__sticky-header"
        :class="[
          $blockClass(blockClass, 'sticky-header'),
          $blockClass(responsiveType, 'sticky-header'),
        ]"
      >
        <div
          ref="dragClose"
          class="overlay__drag-close overlay-handle"
          tabindex="-1"
          aria-hidden="true"
        >
          <div class="overlay-handle__bar" />
        </div>

        <slot name="title" />
      </div>

      <div
        v-else-if="isMobile && mobileDragClose && type === 'shelf-modal'"
        ref="dragClose"
        class="overlay__drag-close overlay-handle"
        tabindex="-1"
        aria-hidden="true"
      >
        <div class="overlay-handle__bar" />
      </div>

      <div
        v-if="template"
        ref="scrollElement"
        class="overlay__container"
        :class="[
          $blockClass(blockClass, 'container'),
          $blockClass(responsiveType, 'container'),
        ]"
      >
        <div
          v-if="
            ($slots.title || showClose) &&
              (!isMobile || !mobileDragClose || type !== 'shelf-modal')
          "
          class="overlay__header"
          :class="[
            {
              'overlay__header--no-title': !$slots.title,
              [$blockClass(blockClass, 'header', 'no-title')]: !$slots.title,
              [$blockClass(responsiveType, 'header', 'no-title')]: !$slots.title,
            },
            $blockClass(blockClass, 'header'),
            $blockClass(responsiveType, 'header'),
          ]"
        >
          <h2
            v-if="$slots.title"
            :id="`${namespace}-title`"
            class="overlay__title text-heading-xl text-heading-2xl-desktop"
            :class="[
              $blockClass(blockClass, 'title'),
              $blockClass(responsiveType, 'title'),
            ]"
          >
            <slot name="title" />
          </h2>

          <div
            v-if="showClose"
            class="overlay__close-container"
            :class="[
              $blockClass(blockClass, 'close-container'),
              $blockClass(responsiveType, 'close-container'),
            ]"
          >
            <button
              class="overlay__close icon-button"
              :class="[
                $blockClass(blockClass, 'close'),
                $blockClass(responsiveType, 'close'),
              ]"
              type="button"
              @click.prevent="close('button')"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.close')"
              />

              <slot name="close">
                <icon-close aria-hidden="true" />
              </slot>
            </button>
          </div>
        </div>

        <div
          class="overlay__body"
          :class="[
            $blockClass(blockClass, 'body'),
            $blockClass(responsiveType, 'body'),
            { 'overlay__body--no-margin': !$slots.title },
          ]"
        >
          <slot
            v-bind="self"
            name="body"
          />
        </div>
      </div>

      <!-- If template prop is false then just render body slot -->
      <div
        v-else
        ref="scrollElement"
        class="overlay__container"
        :class="[
          $blockClass(blockClass, 'container'),
          $blockClass(responsiveType, 'container'),
        ]"
      >
        <slot
          v-bind="self"
          name="body"
        />
      </div>
    </div>
  </transition>

  <teleport
    :disabled="!teleport"
    to="body"
  >
    <transition name="fade">
      <button
        v-if="isActive && showOverlay"
        class="window-overlay"
        :class="[ $blockClass(blockClass, 'backdrop') ]"
        :style="backdropStyles"
        tabindex="-1"
        type="button"
        aria-hidden="true"
        @blur="() => null"
        @click.prevent="closeOnBackdropClick ? close('overlay') : null"
        @focus="() => null"
        @mouseenter="handleOverlayHover"
        @mouseleave="handleOverlayLeave"
      />
    </transition>
  </teleport>

  <overlay-drag
    v-if="isMobile && mobileDragClose"
    ref="overlayDrag"
  />
</template>

<script>

/**
 * Vue: Overlay (overlay)
 * -----------------------------------------------------------------------------
 * Base template for overlays (drawers and modals).
 * - Add `js-overlay="scrollElement"` to element in <slot> to pass that as the
 *   element to disableBodyScroll instead of `.overlay__container`.
 *
 * @param {Boolean} [allowEscape] - Allow closing with escape key.
 * @param {String} [overlayLabel] - accessibility label that is not visible
 * @param {Boolean} [autoQueue] - Automatically opened overlays are queued.
 * @param {Number} [autoShow] - Time in ms to wait before auto-showing overlay.
 * @param {Object} [backdropStyles] - Styles applied to backdrop.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [classContainer] - Class applied to container.
 * @param {Boolean} [closeOnBackdropClick] - Close overlay on backdrop click.
 * @param {Boolean} [closeOnBackdropHover] - Close overlay on backdrop hover.
 * @param {String} direction - Open animation direction set using transition
 * styles, `up`, `down` or ` `fade` for `modal` and `left` or `right` for
 * `drawer`,
 * supports a combination in the format [mobile direction]-[desktop direction].
 * @param {Boolean} [disableScroll] - Trigger `disableBodyScroll` on open.
 * @param {String} [focusReturn] - custom DOM selector to return focus to when
 * closing the overlay.
 * @param {String} namespace - Unique namespace for overlay, reference when
 * opening/closing the overlay.
 * @param {Boolean} [noContent] - Displays no content, only window overlay, you
 * must manually disable scroll and trap focus in the component which triggers
 * this overlay.
 * @param {Boolean} [showClose] - Show the close button, useful for when you
 * want to bind custom events to when the overlay closes.
 * @param {Boolean} [showOverlay] - Show the window overlay?
 * @param {Boolean} [teleport] - Teleport window overlay to end of <body>.
 * @param {Boolean} [template] - Render with default templating.
 * @param {String} type - Type of overlay, `modal`, `drawer`, or `shelf`,
 * supports a combination in the format [mobile type]-[desktop type].
 *
 */
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { mapActions, mapGetters, mapState } from 'vuex'
import OverlayDrag from './overlay-drag'

import IconClose from '~icons/directional-navigation/close-outline.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Overlay',

  components: {
    IconClose,
    OverlayDrag,
  },

  props: {
    allowEscape: {
      type: Boolean,
      default: true,
    },
    overlayLabel: {
      type: String,
      default: '',
    },
    autoQueue: {
      type: Boolean,
      default: true,
    },
    autoShow: {
      type: Number,
      default: 0,
    },
    backdropStyles: {
      type: Object,
      default: () => ({}),
    },
    blockClass: {
      type: String,
      default: '',
    },
    classContainer: {
      type: String,
      default: '',
    },
    closeOnBackdropClick: {
      type: Boolean,
      default: true,
    },
    closeOnBackdropHover: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String,
      default: 'up',
      required: true,
      validator: (value) => {
        return value.split('-').every((part) => ['up', 'down', 'left', 'right', 'fade'].includes(part))
      },
    },
    disableUpdateFocussedElement: {
      type: Boolean,
      default: false,
    },
    disableScroll: {
      type: Boolean,
      default: true,
    },
    focusReturn: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    noContent: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    mobileDragClose: {
      type: Boolean,
      default: false,
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    teleport: {
      type: Boolean,
      default: true,
    },
    template: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'modal',
      required: true,
      validator: (value) => {
        return value.split('-').every((part) => ['drawer', 'modal', 'shelf'].includes(part))
      },
    },
  },

  data() {
    return {
      callback: {
        button: false,
        overlay: false,
      },
      cnvsGlobal: true,
      scrollElement: false,
      timeout: false,
      customFocus: this.focusReturn,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getElementInFocusTrap: 'accessibility/getElementInFocusTrap',
      getFirstElement: 'accessibility/getFirstElement',
      getOverlay: 'overlays/getOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      hydrated: (state) => state.index.hydrated,
      isMobile: (state) => state.index.screen.mobile,
      previousElement: (state) => state.accessibility.previousElement,
    }),

    /**
     * Compute if overlay has sticky header.
     * @returns {Boolean}
     */
    hasStickyHeader() {
      return (
        this.isMobile &&
        this.mobileDragClose &&
        this.type === 'shelf-modal' &&
        this.$slots.title
      )
    },

    /**
     * Compute overlay active state.
     * @returns {Boolean}
     */
    isActive() {
      return this.getActiveOverlay?.namespace === this.namespace
    },

    /**
     * Compute responsive direction based on mobile.
     * @returns {String}
     */
    responsiveDirection() {
      if (!this.direction.includes('-')) {
        return this.direction
      }

      return this.isMobile
        ? this.direction.split('-')[0]
        : this.direction.split('-')[1]
    },

    /**
     * Compute responsive type based on mobile.
     * @returns {String}
     */
    responsiveType() {
      if (!this.type.includes('-')) {
        return this.type
      }

      return this.isMobile ? this.type.split('-')[0] : this.type.split('-')[1]
    },

    /**
     * Compute overlay API.
     * @returns {Object}
     */
    self() {
      return {
        ...this.$data,
        ...this.$props,
        isActive: this.isActive,
        addCallback: this.addCallback,
        close: (type) => this.close(type),
        open: (args) => this.openOverlay({ namespace: this.namespace, ...args }),
        update: this.update,
        // Expose overlay ref too.
        ref: this.$refs.overlay,
      }
    },

    /**
     * Compute transition name based on type.
     * @returns {String}
     */
    transitionName() {
      const type = this.responsiveType === 'modal' ? 'fade' : 'slide'
      return `${type}-${this.responsiveDirection}`
    },
  },

  watch: {

    /**
     * Watch hydrated state to start auto-show countdown.
     */
    hydrated() {
      this.setAutoShow()
    },

    /**
     * Watch active state.
     * - Separate logic for event binding.
     */
    async isActive() {
      if (this.noContent) {
        return
      }

      /**
       * If active then:
       * - Set previous element (to focus on close).
       * - Disable body scroll (if applicable).
       * - Reset scroll element scroll position.
       * - Trap focus inside overlay element.
       * - Focus first trap focus element.
       */
      if (this.isActive) {
        const focusEl = this.customFocus ? document.querySelector(this.customFocus) : false
        const activeEl = focusEl ? focusEl : document.activeElement
        this.setPreviousElement(activeEl)

        if (this.disableScroll) {
          this.getScrollElement()
          disableBodyScroll(this.scrollElement, this.bodyScrollOptions())
        }

        await this.$nextTick()

        if (this.scrollElement) {
          this.scrollElement.scrollTo(0, 0)
        }

        this.initializeMobileDragListener()
        this.updateFocusTrap()
        return
      }

      if (this.disableScroll) {
        if (this.scrollElement) {
          enableBodyScroll(this.scrollElement)
        } else {
          clearAllBodyScrollLocks()
        }
      }

      /**
       * If no other active overlay when closing then clear focus trap.
       */
      if (!this.getActiveOverlay) {
        this.clearFocusTrap()
      }

      /**
       * Set focus back to previous element if set.
       * - Also reset previous element state to false.
       */
      if (!this.previousElement || typeof this.previousElement !== 'object') {
        return
      }

      cnvs.Focus(this.previousElement)
      if (!this.customFocus) {
        this.setPreviousElement()
      }
    },
  },

  mounted() {
    this.setEventBusListeners()
    this.setAutoShow()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      queueOverlay: 'overlays/queue',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPreviousElement: 'accessibility/setPreviousElement',
    }),

    /**
     * Get scroll element.
     */
    getScrollElement() {
      if (!this.$refs.scrollElement) {
        this.scrollElement = false
        return
      }

      this.scrollElement =
        this.$refs.scrollElement.querySelector('[js-overlay="scrollElement"]') ||
        this.$refs.scrollElement
    },

    /**
     * Set modal to autoshow.
     */
    async setAutoShow() {
      if (!this.autoShow || !this.hydrated) {
        return
      }

      await cnvs.Sleep(this.autoShow)

      if (this.autoQueue) {
        this.queueOverlay({ namespace: this.namespace })
        return
      }

      this.openOverlay({ namespace: this.namespace })
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      if (this.allowEscape) {
        cnvs.EventBus.listen('cnvs:esc', () => {
          if (!this.isActive) {
            return
          }

          this.close('esc')
        })
      }

      cnvs.EventBus.listen('overlay:customFocus', (focusEl) => {
        this.customFocus = focusEl
      })

      cnvs.EventBus.listen('overlay:update', (namespace) => {
        if (!this.isActive || namespace !== this.namespace) {
          return
        }

        this.update()
      })
    },

    /**
     * Update body scroll element and focus trap.
     * - Can only be triggered when overlay is active.
     * @param {Obect} [args] - Arguments passed on overlay API update.
     */
    async update(args) {
      if (!this.isActive) {
        return
      }

      await this.$nextTick()
      this.updateFocusTrap(args)
      this.updateBodyScroll()
    },

    /**
     * Update focus trap using Vuex.
     * @param {HTMLElement} [currentElement] - Current focussed element to
     * maintain focus on whilst updating focus trap, if it still exists.
     * @param {Object} [focusElements] - Additional focus trap elements, these
     * are used instead of any focusElements defined when opening the overlay.
     * See overlays.js for the JSDoc definition of focusElements.
     */
    updateFocusTrap({ currentElement, focusElements } = {}) {
      const overlay = this.getOverlay(this.namespace)
      const customFocusEl = this.customFocus ? document.querySelector(this.customFocus) : false
      const currentEl = customFocusEl ? customFocusEl : currentElement

      const usedfocusElements = focusElements
        ? focusElements
        : overlay?.focusElements || {}

      const config = {
        ...usedfocusElements,
        container: [this.$refs.overlay],
      }

      if (Array.isArray(usedfocusElements?.container)) {
        config.container = usedfocusElements.prepend
          ? [...usedfocusElements.container, this.$refs.overlay]
          : [this.$refs.overlay, ...usedfocusElements.container]
      }

      this.setFocusTrap(config)

      /**
       * Update focussed element.
       * - Have to use timeout to wait for animation to complete as Safari
       *   doesn't support `preventScroll`.
       * - Without timeout the focus would jump instead of smoothly scroll.
       */
      const elementToFocus = this.getElementToFocus(currentEl)

      if (!elementToFocus || this.disableUpdateFocussedElement) {
        return
      }

      setTimeout(() => {
        elementToFocus.focus({ preventScroll: true })
      }, this.$timing('normal'))
    },

    /**
     * Get element to focus inside focus trap.
     * @param {HTMLElement} [currentElement] - Current focussed element to
     * maintain focus on whilst updating focus trap, if it still exists.
     * @returns {HTMLElement|Null}
     */
    getElementToFocus(currentElement) {
      if (!currentElement || !this.getElementInFocusTrap(currentElement)) {
        return this.getFirstElement
      }

      return currentElement
    },

    /**
     * Update body scroll element.
     */
    updateBodyScroll() {
      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement)
      } else {
        clearAllBodyScrollLocks()
      }

      this.getScrollElement()

      if (this.$variable('storybook') && !this.scrollElement) {
        return
      }

      disableBodyScroll(this.scrollElement)
    },

    /**
     * Define body scroll options.
     * - Adds support for [js-body-scroll-lock-ignore] attribute which allows
     *   element to maintain scrolling when disableBodyScroll is enabled.
     * @returns {Object}
     */
    bodyScrollOptions() {
      return {
        // eslint-disable-next-line consistent-return
        allowTouchMove: (element) => {
          const ignoredElement = element.closest('[js-body-scroll-lock-ignore]')
          return ignoredElement
        },
      }
    },

    /**
     * Add callback function to run after close.
     * @param {Function} callback - Function to use as callback on close.
     * @param {String} [type] - Optional, either `button` or `overlay` to define
     * when callback should be run.
     */
    addCallback(callback, type) {
      if (!type) {
        this.callback.button = callback
        this.callback.overlay = callback
        return
      }

      this.callback[type] = callback
    },

    /**
     * Handle overlay close.
     * @param {String} type - Type of close event, `button`, `esc` or `overlay`,
     * used to control callback function.
     */
    close(type) {
      this.closeOverlay(this.namespace)

      cnvs.EventBus.emit('overlay:customFocus', this.focusReturn)
      if (typeof this.callback[type] !== 'function') {
        return
      }

      this.callback[type]()
    },

    /**
     * Handle overlay hover.
     */
    handleOverlayHover() {
      if (!this.closeOnBackdropHover) {
        return
      }

      this.timeout = setTimeout(() => {
        this.close('overlay')
      }, 150)
    },

    /**
     * Handle overlay leave.
     */
    handleOverlayLeave() {
      if (!this.closeOnBackdropHover) {
        return
      }

      clearTimeout(this.timeout)
    },

    /**
     * Start drag listener component
     */
    initializeMobileDragListener() {
      if (!this.$refs.overlayDrag) {
        return
      }

      this.$refs.overlayDrag.initiateDragClose(this.$refs.overlay, this.namespace)
    },

    /**
     * Listen touch start
     * @param event
     */
    dragTouchStart(event) {
      this.$refs?.overlayDrag?.started(event)
    },

    /**
     * Listen touch move
     * @param event
     */
    dragTouchMoved(event) {
      if (!this.$refs.overlayDrag) {
        return
      }

      if (event.target === this.$refs.dragClose) {
        this.$refs.overlayDrag.dragMoved(
          event,
          { top: true, bottom: true },
        )

        return
      }

      this.$refs.overlayDrag.dragMoved(
        event,
        this.getScrollableYPosition(),
      )
    },

    /**
     * Listen touch ended
     * @param event
     */
    dragTouchEnded(event) {
      this.$refs?.overlayDrag?.dragEnded(event)
    },

    /**
     * Check scrollable position, used for components which have vertical scroll
     */
    getScrollableYPosition() {
      const scrollable = this.$refs.scrollElement
      const reachedTop = scrollable.scrollTop === 0
      const reachedBottom =
        scrollable.scrollTop === (scrollable.scrollHeight - scrollable.offsetHeight)

      return { top: reachedTop, bottom: reachedBottom }
    },
  },
}
</script>
